import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { LogOut, Briefcase, Wrench, BookOpen, HelpCircle, Inbox, ExternalLink, FileDown, Images } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { RESOURCES } from "./admin/resourceSchemas";
import ResourceManager from "./admin/ResourceManager";
import ContactRequests from "./admin/ContactRequests";

const TABS = [
  { id: "contact", label: "Cereri primite", icon: Inbox },
  { id: "services", label: "Servicii", icon: Briefcase },
  { id: "tools", label: "Echipament", icon: Wrench },
  { id: "case-studies", label: "Studii de caz", icon: BookOpen },
  { id: "gallery", label: "Cartografie", icon: Images },
  { id: "faqs", label: "Întrebări", icon: HelpCircle },
];

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("contact");

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-neutral-400 font-mono-tech text-sm">
        Verificare sesiune...
      </div>
    );
  }

  if (!user) return <Navigate to="/admin/login" replace />;

  const onLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const renderTab = () => {
    if (tab === "contact") return <ContactRequests />;
    const res = RESOURCES[tab];
    if (!res) return null;
    return <ResourceManager resource={res} key={tab} />;
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="bg-black/80 backdrop-blur-xl border-b border-neutral-900 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-8 w-8 items-center justify-center bg-yellow-500 text-black font-black font-display">
              L
            </span>
            <div className="leading-tight">
              <div className="font-mono-tech text-[10px] text-neutral-500 uppercase tracking-[0.2em]">
                Panou administrare
              </div>
              <div className="font-display font-semibold text-sm">{user.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/manual-utilizare.pdf"
              target="_blank"
              rel="noopener noreferrer"
              download
              data-testid="download-manual"
              className="hidden md:inline-flex items-center gap-2 btn-outline px-3 py-2 text-[11px] font-mono-tech uppercase tracking-[0.15em] hover:border-yellow-500 hover:text-yellow-400"
            >
              <FileDown className="h-3.5 w-3.5" /> Manual PDF
            </a>
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-2 btn-outline px-3 py-2 text-[11px] font-mono-tech uppercase tracking-[0.15em]"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Vezi site
            </a>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 btn-outline px-3 py-2 text-[11px] font-mono-tech uppercase tracking-[0.15em]"
            >
              <LogOut className="h-3.5 w-3.5" /> Ieșire
            </button>
          </div>
        </div>
      </header>

      <div className="border-b border-neutral-900 bg-[#080808]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-1 overflow-x-auto" data-testid="admin-tabs">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = t.id === tab;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`tab-${t.id}`}
                  className={`flex items-center gap-2 px-4 py-4 text-xs font-mono-tech uppercase tracking-[0.15em] whitespace-nowrap border-b-2 transition-colors ${
                    active
                      ? "text-yellow-400 border-yellow-400"
                      : "text-neutral-500 border-transparent hover:text-neutral-200"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {t.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">{renderTab()}</main>
    </div>
  );
}
